import "./styles/index.css";
import "./i18n";

import { captureConsoleIntegration, init as SentryInit } from "@sentry/react";
import { NeuronProvider, SPAConfig } from "@sphtech/neuron";
import { ReactElement, StrictMode, useEffect } from "react";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useMatches,
} from "react-router";

import neuronModules from "../neuron.config.json";
import { initGTM } from "./analytics/gtm";
import AppContextHOC from "./components/AppContextHOC";
import Hotjar from "./providers/Hotjar";
import NewRelic from "./providers/NewRelic";
import Vwo from "./providers/Vwo";

SentryInit({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  maxBreadcrumbs: 50,
  integrations: [captureConsoleIntegration({ levels: ["error"] })],
  environment: import.meta.env.VITE_ENVIRONMENT,
  enabled: import.meta.env.MODE !== "development",
  sampleRate: 0.05,
});

const neuronConfig: SPAConfig = {
  sessionApiUrl: import.meta.env.VITE_NEURON_SESSION_API_URL,
  sessionApiToken: import.meta.env.VITE_NEURON_SESSION_API_TOKEN,
  segmentsApiUrl: import.meta.env.VITE_SEGMENTO_API_URL,
  ...neuronModules,
};

// Layout replaces index.html — renders the <html> shell
export function Layout({ children }: { children: React.ReactNode }) {
  const matches = useMatches();

  // Skip <Scripts /> for static error pages (hydrate: false)
  const includeScripts = matches.every((match) => {
    const handle = match.handle as { hydrate?: boolean } | undefined;
    return handle?.hydrate == null || handle.hydrate;
  });

  // Read lang from the matched route's loader data, fall back to "en"
  const lang = matches.reduce<string>((acc, match) => {
    const data = match.data as { lang?: string } | undefined;
    return data?.lang ?? acc;
  }, "en");

  return (
    <html lang={lang}>
      <head>
        <meta charSet="UTF-8" />
        <link href="/favicon.ico" rel="icon" type="image/x-icon" />
        <meta content="width" name="MobileOptimized" />
        <meta content="true" name="HandheldFriendly" />
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <meta content="ie=edge" httpEquiv="x-ua-compatible" />
        <meta
          name="google-site-verification"
          content="WJz_lO68TW2Vfo18N9JuDjBEwzxidEuMd5BCQg0SWwM"
        />
        <script
          dangerouslySetInnerHTML={{
            __html: "window.dataLayer = window.dataLayer || [];",
          }}
        />
        <Meta />
        <Links />
      </head>
      <body>
        <div id="app">{children}</div>
        <ScrollRestoration />
        {includeScripts ? <Scripts /> : null}
      </body>
    </html>
  );
}

// App wraps all routes with providers (replaces App.tsx)
export default function App(): ReactElement {
  const matches = useMatches();

  const isStaticPage = matches.some((match) => {
    const handle = match.handle as { hydrate?: boolean } | undefined;
    return handle?.hydrate === false;
  });

  useEffect(() => {
    initGTM();
  }, []);

  if (isStaticPage) {
    return <Outlet />;
  }

  return (
    <StrictMode>
      <NewRelic />
      <Hotjar />
      <Vwo />
      <AppContextHOC>
        <NeuronProvider config={neuronConfig}>
          <Outlet />
        </NeuronProvider>
      </AppContextHOC>
    </StrictMode>
  );
}

export function ErrorBoundary({ error }: { error: unknown }) {
  if (isRouteErrorResponse(error)) {
    return (
      <div>
        <h1>
          {error.status} {error.statusText}
        </h1>
        <p>{error.data}</p>
      </div>
    );
  } else if (error instanceof Error) {
    return (
      <div>
        <h1>Error</h1>
        <p>{error.message}</p>
        <pre>{error.stack}</pre>
      </div>
    );
  }
  return <h1>Unknown Error</h1>;
}
